<template>
	<v-sheet rounded>
		<v-card color="#125B92">
			<v-card-title v-if="titleBar" class="align-center">
				<v-icon left large color="#E3ECF2">mdi-folder</v-icon>
				<h3 class="white--text">{{ title }}</h3>
				<v-spacer />
				<slot />
			</v-card-title>

			<v-divider v-if="titleBar" class="thick" />

			<v-data-table
				v-model="selectedItems"
				:headers="tableHeaders"
				:items="items"
				:loading="loading"
				:items-per-page.sync="filters.paginated"
				:footer-props="{
					'items-per-page-options': [15, 30, 45, 60, 75]
				}"
				:page.sync="filters.page"
				:server-items-length="totalInvoices"
				:sort-by.sync="filters.sort"
				:sort-desc.sync="filters.descending"
				:item-class="invoiceStatusBackground"
				class="table-stripped"
				:show-select="actionable"
				@update:page="$vuetify.goTo(0)">
				<template #top="{ pagination, options, updateOptions }">
					<v-toolbar flat>
						<v-menu v-if="actionable" offset-y>
							<template #activator="{ on, attrs }">
								<v-btn
									color="primary"
									elevation="0"
									:disabled="! length(selectedItems)"
									class="mr-3"
									v-bind="attrs"
									v-on="on">
									Bulk Actions
									<v-icon right>mdi-menu-down</v-icon>
								</v-btn>
							</template>
							<v-list class="py-0">
								<v-list-item dense>
									<v-list-item-subtitle>{{ length(selectedItems) }} invoice selected</v-list-item-subtitle>
								</v-list-item>
								<v-divider />
								<v-list-item link @click="sendStatement">
									<v-list-item-title>Send Statement</v-list-item-title>
									<v-list-item-icon><v-icon>mdi-receipt-text-arrow-right</v-icon></v-list-item-icon>
								</v-list-item>
							</v-list>
						</v-menu>
						<v-spacer v-if="actionable && !topPaging" />
						<div class="flex-fill">
							<v-text-field
								v-model.trim="filters.search"
								class="table-search"
								append-icon="mdi-magnify"
								placeholder="Search..."
								name="search"
								autocomplete="off"
								single-line
								clearable
								filled
								outlined
								dense
								full-width
								autofocus
								hide-details />
						</div>
						<div v-if="! simplify" class="ml-3">
							<v-select
								v-model="filters.status"
								:items="statuses"
								placeholder="Filter by Status"
								item-text="name"
								item-value="value"
								single-line
								clearable
								filled
								outlined
								dense
								hide-details></v-select>
						</div>
						<div v-if="topPaging">
							<v-data-footer
								:pagination="pagination"
								:options="options"
								:items-per-page-options="[15, 30, 45, 60, 75]"
								items-per-page-text="$vuetify.dataTable.itemsPerPageText"
								@update:options="updateOptions" />
						</div>
					</v-toolbar>
				</template>
				<template #item.id="{ item }">
					<Link :href="route('invoices.show', item)">#{{ item.id }}</Link>
				</template>

				<template #item.status="{ item }">
					<v-chip small
						label
						:color="item.status.color"
						dark>
						{{ item.status.name }}
					</v-chip>
				</template>

				<template #item.project_id="{ item }">
					<Link v-if="item.project" :href="route('projects.show', item.project)">{{ item.project.id }}</Link>
					<span v-else>--</span>
				</template>

				<template #item.project_name="{ item }">
					<Link v-if="item.project" :href="route('projects.show', item.project)">
						<span v-if="item.project.name">{{ item.project.name }}</span>
						<em v-else class="grey--text text--darken-2">Unknown Name</em>
					</Link>
					<span v-else>--</span>
				</template>

				<template #item.project_type="{ item }">
					<span v-if="item.project">{{ item.project.type }}</span>
					<span v-else>--</span>
				</template>

				<template #item.client="{ item }">
					<span v-if="item.project && item.project.client">
						<Link :href="route('clients.show', item.project.client)">
							{{ item.project.client.name }}
						</Link>
					</span>
					<span v-else>--</span>
				</template>

				<template #item.total="{ item }">
					<span class="font-mono">{{ item.total | money }}</span>
				</template>

				<template #item.total_due="{ item }">
					<span class="font-mono">{{ item.total_due | money }}</span>
				</template>

				<template #item.orderer="{ item }">
					<span v-if="item.project && item.project.orderer">{{ item.project.orderer.name }}</span>
					<span v-else>--</span>
				</template>

				<template #item.created_at="{ item }">
					{{ item.created_at | localDate }}
				</template>

				<template #item.aging="{ item }">
					{{ item.aging }} days
				</template>

				<template #item.actions="{ item }">
					<v-menu offset-y close-on-click close-on-content-click>
						<template #activator="{ on, attrs }">
							<v-btn
								color="primary"
								dark
								icon
								v-bind="attrs"
								v-on="on">
								<v-icon>mdi-dots-vertical</v-icon>
							</v-btn>
						</template>
						<v-list>
							<create-payment list-item :invoice-id="item.id">Add Payment</create-payment>
							<delete-button
								list-item
								model="invoices"
								:model-id="item.id"
								:only="['invoices', 'counts', 'flash']">
								Delete Invoice
							</delete-button>
						</v-list>
					</v-menu>
				</template>
			</v-data-table>
		</v-card>

		<send-invoice-statement v-model="invoiceStatementDialog" :invoices="selectedItems" />
	</v-sheet>
</template>
<script>
import _ from 'lodash'
import api from '@/api'
import DeleteButton from '@/Pages/Backend/Components/DeleteButton'
import SendInvoiceStatement from '@/Pages/Backend/Components/Dialogs/SendInvoiceStatement.vue'
import CreatePayment from '@/Pages/Backend/Payments/Create'

export default {
	components: { CreatePayment, DeleteButton, SendInvoiceStatement },
	props: {
		title: {
			type: String,
			default: 'Invoices'
		},
		invoices: {
			type: [Array, Object],
			default: () => {},
			required: true
		},
		simplify: {
			type: Boolean,
			default: () => false
		},
		titleBar: {
			type: Boolean,
			default: () => false
		},
		topPaging : {
			type: Boolean,
			default: () => true
		},

		/**
		 * This toggles the selectable rows and "bulk action" button. In the future
		 * we may need to change this to support multiple different actions.
		 */
		actionable: {
			type: Boolean,
			default: () => false
		}
	},
	data () {
		return {
			filters: {
				search: '',
				status: '',
				paginated: 30,
				page: 1,
				descending: true,
				sort: 'id'
			},
			statuses: [],
			loading: true,
			items: this.invoices.data,
			selectedItems: [],
			invoiceStatementDialog: false,
			tableHeaders: [
				{
					text: '#',
					align: 'start',
					value: 'id'
				},
				{
					text: 'Project',
					align: 'center',
					value: 'project_id'
				},
				{
					text: 'Project Name',
					sortable: false,
					align: 'start',
					value: 'project_name'
				},
				{
					text: 'Status',
					sortable: true,
					align: 'center',
					value: 'status'
				},
				{
					text: 'Total',
					sortable: false,
					align: 'center',
					value: 'total'
				},
				{
					text: 'Total Due',
					sortable: false,
					align: 'center',
					value: 'total_due'
				},
				{
					text: 'Client',
					sortable: false,
					align: 'start',
					value: 'client'
				},
				{
					text: 'Orderer',
					sortable: false,
					align: 'start',
					value: 'orderer'
				},
				{
					text: 'Project Type',
					sortable: false,
					align: 'start',
					value: 'project_type'
				},
				{
					text: 'Sent',
					align: 'start',
					value: 'created_at'
				},
				{
					text: 'Aging',
					sortable: false,
					align: 'start',
					value: 'aging'
				},
				{
					text: '',
					sortable: false,
					align: 'end',
					value: 'actions'
				}
			]
		}
	},
	computed: {
		totalInvoices () {
			if (this.invoices && this.invoices.hasOwnProperty('meta')) {
				return this.invoices.meta.total
			}

			return 0
		}
	},
	watch: {
		'filters.search': {
			handler: _.debounce(function (newVal, oldVal) {
				if (newVal && newVal !== oldVal || !newVal && oldVal) {
					this.getInvoices()
				}
			}, 350)
		},

		'$page.props.filters.status' (newVal, oldVal) {
			this.filters.status = newVal
		},

		invoices () {
			if (this.invoices.hasOwnProperty('data')) {
				this.items = this.invoices.data
				this.loading = false
			} else {
				this.items = []
			}
		}
	},
	mounted () {
		this.getInvoicesStatuses()

		if (this.invoices.hasOwnProperty('data')) {
			this.items = this.invoices.data
			this.loading = false
		}

		if (this.$page.props.filters) {
			this.filters = this.$page.props.filters
		}

		this.$watch(vm => [vm.filters.paginated, vm.filters.page, vm.filters.status, vm.filters.sort, vm.filters.descending], val => {
			this.getInvoices()
		})

		if (this.simplify) {
			// Remove some default columns
			this.tableHeaders = _.remove(this.tableHeaders, header => !['project', 'project_type', 'client', 'orderer', 'created_at'].includes(header.value))
		} else {
			this.tableHeaders = _.remove(this.tableHeaders, header => !['project_name'].includes(header.value))
		}
	},
	methods: {
		getInvoices () {
			this.$inertia.reload({
				onStart: () => this.loading = true,
				onFinish: () => {
					this.loading = false

					if(! this.length(this.items) && this.filters.page > 1) {
						this.filters.page = 1
					}
				},
				only: ['invoices'],
				data: {
					paginated: this.filters.paginated,
					page: this.filters.page,
					search: this.filters.search,
					status: this.filters.status,
					sort: this.filters.sort,
					descending: this.filters.descending
				}
			})
		},

		getInvoicesStatuses () {
			const method = 'GET'
			const data = {}

			const url = this.route('invoices.api.statuses')

			return api({
				method,
				url,
				data
			}).then(response => {
				this.statuses = [
					...response.data,
					...[{ // this is not an assignable status, but rather just a filter for admins
						'name': 'Outstanding',
						'value': 'outstanding',
						'color': '#000000'
					}]
				]
			})
		},

		invoiceStatusBackground (item) {
			if (item.status.value === 'paid' || item.status.value === 'draft') {
				return item.status.css + ' lighten-5'
			}

			if (item.aging <= 30) {
				return 'white'
			}

			if (item.aging <= 60) {
				return 'blue lighten-5'
			}

			if (item.aging > 60) {
				return 'red lighten-5'
			}
		},

		sendStatement () {
			this.invoiceStatementDialog = true
		}
	}
}
</script>
